import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout/Layout";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2 } from "lucide-react";
import { ProfileHeader } from "@/components/User/ProfileHeader";
import { GeoSignalsBar } from "@/components/User/GeoSignalsBar";
import { ProfileTabs, ProfileTabContent } from "@/components/User/ProfileTabs";
import { AboutTab } from "@/components/User/AboutTab";
import { ProfilePromptsGrid } from "@/components/User/ProfilePromptsGrid";
import { SavedPromptsGrid } from "@/components/User/SavedPromptsGrid";
import { CollectionsTab } from "@/components/User/CollectionsTab";
import { EditProfileModal } from "@/components/User/EditProfileModal";
import { useCreatorStats } from "@/hooks/user/useCreatorStats";

const Profile = () => {
  const { user, userProfile, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("prompts");
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const stats = useCreatorStats(user?.id || "");

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/prompts");
    }
  };

  const handleSaveProfile = async (data: {
    username: string;
    bio: string;
    tagline: string;
    longBio: string;
    focusTags: string[];
    externalLinks: Array<{ label: string; url: string }>;
    avatarId: number;
  }) => {
    if (!userProfile) return;
    
    await updateProfile({
      ...userProfile,
      username: data.username,
      bio: data.bio,
      tagline: data.tagline,
      longBio: data.longBio,
      focusTags: data.focusTags,
      externalLinks: data.externalLinks,
      avatarId: data.avatarId,
    });
  };

  if (!user || !userProfile) {
    return (
      <Layout>
        <div className="container max-w-4xl mx-auto px-4 py-8">
          <div className="flex justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={handleBack}
          className="text-muted-foreground hover:text-foreground -ml-2"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        {/* Profile Header */}
        <ProfileHeader
          userId={user.id}
          username={userProfile.username}
          avatarId={userProfile.avatarId}
          bio={userProfile.bio}
          tagline={userProfile.tagline}
          promptCount={stats.totalPrompts}
          isOwnProfile={true}
          badges={userProfile.badges}
          creatorTier={stats.creatorTier}
          onEditProfile={() => setIsEditModalOpen(true)}
        />

        {/* GEO Signals Bar */}
        <GeoSignalsBar
          totalPrompts={stats.totalPrompts}
          totalCopies={stats.totalCopies}
          avgGeoScore={stats.avgGeoScore}
          creatorTier={stats.creatorTier}
          lastActive={stats.lastActive}
        />

        {/* Profile Tabs */}
        <ProfileTabs activeTab={activeTab} onTabChange={setActiveTab} isOwnProfile={true}>
          <ProfileTabContent value="prompts">
            <ProfilePromptsGrid userId={user.id} isOwnProfile={true} />
          </ProfileTabContent>
          
          <ProfileTabContent value="saved">
            <SavedPromptsGrid userId={user.id} />
          </ProfileTabContent>
          
          <ProfileTabContent value="collections">
            <CollectionsTab userId={user.id} isOwnProfile={true} />
          </ProfileTabContent>
          
          <ProfileTabContent value="about">
            <AboutTab 
              bio={userProfile.bio}
              longBio={userProfile.longBio}
              focusTags={userProfile.focusTags}
              externalLinks={userProfile.externalLinks}
              createdAt={userProfile.createdAt}
              username={userProfile.username}
              isOwnProfile={true}
              onEditProfile={() => setIsEditModalOpen(true)}
            />
          </ProfileTabContent>
        </ProfileTabs>

        {/* Edit Profile Modal */}
        <EditProfileModal
          open={isEditModalOpen}
          onOpenChange={setIsEditModalOpen}
          userId={user.id}
          initialData={{
            username: userProfile.username,
            bio: userProfile.bio,
            tagline: userProfile.tagline || "",
            longBio: userProfile.longBio || "",
            focusTags: userProfile.focusTags || [],
            externalLinks: userProfile.externalLinks || [],
            avatarId: userProfile.avatarId,
          }}
          onSave={handleSaveProfile}
        />
      </div>
    </Layout>
  );
};

export default Profile;
